import React, { useState, useMemo, useEffect } from "react";
import MonthDropdown from "./MonthDropdown";
import YearDropdown from "./YearDropdown";
import "../../App.css";
import {
  DateFormat,
  DisplayMode,
  IAvailableSlot,
  IInterviewDate,
  IReservedSlot,
  defaultWorkingHours,
  getDatesArray,
  getFormattedHours,
  getHourBounds,
} from "./helper";
import AvailabilityTable from "./AvailablilityTable";
import { axiosInstant, endpoints } from "../../api";
import { useSearchParams } from "react-router-dom";
import { Col, Row, Space, Typography, Checkbox, Alert } from "antd";
import { IApplicant } from "../ManagersPanel/helper";
import ScheduleMeetingModal from "./ScheduleMeetingModal";
import { OfferSlotsPanel, OverlapSuggestions } from "./SlotSuggestions";
import { TimezoneNote } from "./TimezoneNote";
import { loadLocalMonth, localizeSlots, utcMonthsFor } from "./slotTime";
import { showError } from "../../Notifications";
import BackBtn from "../backBtn";
import { useUser } from "../../context";

const AvailabilityCalendar = () => {
  const [searchParams] = useSearchParams();
  const jobApplicationId = searchParams.get("jobApplicationId");
  const { user } = useUser();

  // Determine mode
  const applicantId = searchParams.get("applicantId");
  const jobId = searchParams.get("jobId");
  const userId = searchParams.get("userId");
  const managerId = searchParams.get("managerId");
  // recruiterMode: recruiter scheduling 1st interview directly with candidate
  const isRecruiterMode = searchParams.get("recruiterMode") === "true";
  const candidateUserId = searchParams.get("candidateUserId") || "";
  const candidateNameParam = searchParams.get("candidateName") || "";
  // isManagerMode: when applicantId and jobId are present (recruiter scheduling with candidate + managers)
  const isManagerMode = !!(applicantId && jobId) && !isRecruiterMode;
  // isCandidateMode: fallback when only userId and jobApplicationId are present
  const isCandidateMode = !!(userId && jobApplicationId) && !isManagerMode && !isRecruiterMode;

  const [candidate, setCandidate] = useState<IApplicant>();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedHours, setSelectedHours] = useState(defaultWorkingHours);
  const [defaultSlots, setDefaultSlots] = useState<Array<IAvailableSlot>>([]);
  const [reservedSlots, setReservedSlots] = useState<Array<IReservedSlot>>([]);
  const [loading, setLoading] = useState(true);
  const [interviewDate, setInterviewDate] = useState<IInterviewDate>();
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [secondInterviewDate, setSecondInterviewDate] = useState<IInterviewDate>();
  const [calendarUsers, setCalendarUsers] = useState<{ label: string, value: string }[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [allAvailabilities, setAllAvailabilities] = useState<{ [userId: string]: IAvailableSlot[] }>({});
  // Derived, not stored. As state written from an effect this could be - and was -
  // overwritten by a later effect run, which showed the slots and then blanked
  // them. As a memo it is always exactly the intersection of the current
  // selection, so there is no window in which it can be wrong.
  const intersectedSlots = useMemo<IAvailableSlot[]>(() => {
    if (selectedUserIds.length === 0) return [];
    return intersectSlots(selectedUserIds.map((id) => allAvailabilities[id] || []));
  }, [selectedUserIds, allAvailabilities]);
  const [job, setJob] = useState<any>(null);
  // Tracks whether the participant checkboxes have been given their initial value,
  // so a re-run of the availability fetch never clobbers the recruiter's choice.
  const selectionSeeded = React.useRef(false);
  const refreshCalendar = () => setRefreshFlag(flag => !flag);

  // In the checkbox view (manager/recruiter mode) the slots shown are driven entirely by the
  // selected participants: intersectedSlots holds the intersection of all selected users'
  // available slots. In candidate mode, show the candidate's own slots.
  const displaySlots = isManagerMode ? intersectedSlots : defaultSlots;

  const datesArray = useMemo(() => {
    return getDatesArray(selectedMonth, DisplayMode.EVERYDAY, selectedYear).filter((e) => {
      const dateString = e.format(DateFormat);
      return (
        displaySlots.some((slot) => slot.date === dateString) ||
        reservedSlots.some((slot) => slot.date === dateString)
      );
    });
  }, [displaySlots, reservedSlots, selectedMonth, selectedYear]);

  // Always fetch candidate info if applicantId is present
  useEffect(() => {
    if (isManagerMode && applicantId) {
      // Keep loading=true — manager mode loading is resolved by the availability fetch effect
      axiosInstant.get(`/users/${applicantId}`)
        .then((res) => {
          if (res.data) setCandidate(res.data);
        })
        .catch((err) => console.log(err.message));
    }
  }, [isManagerMode, applicantId]);

  useEffect(() => {
    // Skip this effect in manager mode — manager mode fetches multi-user availability separately
    if (jobId && !isManagerMode) {
      setLoading(true);
      loadLocalMonth(selectedMonth, selectedYear, async (utcMonth, utcYear) =>
        (await axiosInstant.get(`/calendar/availability/${jobId}?month=${utcMonth}&year=${utcYear}`)).data
      )
        .then((res) => {
          const { availableSlots = [], reservedSlots = [] } = res || {};
          setDefaultSlots(availableSlots);
          setReservedSlots(reservedSlots);
          setSelectedHours(getHourBounds(availableSlots));
        })
        .catch((err) => showError(err))
        .finally(() => setLoading(false));
    }
  }, [jobId, isManagerMode, selectedMonth, selectedYear, refreshFlag]);

  // CANDIDATE MODE: fetch candidate info and slots (fallback when not in manager mode)
  useEffect(() => {
    if (isCandidateMode && userId) {
      setLoading(true);
      axiosInstant.get(`/users/${userId}`)
        .then((res) => {
          if (res.data) setCandidate(res.data);
        })
        .catch((err) => console.log(err.message));
    }
  }, [isCandidateMode, userId]);

  useEffect(() => {
    if (isCandidateMode && userId) {
      setLoading(true);
      const monthForBackend = selectedMonth; // 0-based
      loadLocalMonth(monthForBackend, selectedYear, async (utcMonth, utcYear) =>
        (await axiosInstant.get(endpoints.getCandidateCalendar(userId!, utcMonth - 1, utcYear))).data
      )
        .then((res) => {
          const { availableSlots = [], reservedSlots = [] } = res || {};
          setDefaultSlots(availableSlots);
          setReservedSlots(reservedSlots);
          setSelectedHours(getHourBounds(availableSlots));
        })
        .catch((err) => showError(err))
        .finally(() => setLoading(false));
    }
  }, [isCandidateMode, userId, selectedMonth, selectedYear, refreshFlag]);

  useEffect(() => {
    if (isManagerMode && job && candidate) {
      // Prepare user list: all managers + candidate
      const managers = (job?.managers?.map((m: any) => ({ label: m.name, value: m._id })) || []);
      const candidateUser = { label: candidate.name, value: candidate._id };
      const users = [...managers, candidateUser];
      setCalendarUsers(users);
      // Seed the selection once only. This effect used to reset it to
      // candidate-only on every run, so any re-run - a month change, or a late
      // job/candidate fetch resolving - silently unchecked the participants the
      // recruiter had just picked. The slots appeared and then vanished.
      if (!selectionSeeded.current) {
        selectionSeeded.current = true;
        // Candidate-only to start, so slots are visible even when no manager has
        // set availability. The recruiter narrows from there.
        setSelectedUserIds([candidate._id]);
      }

      setLoading(true);
      // Each UTC month the local month touches, then converted to the viewer's zone.
      Promise.all(
        utcMonthsFor(selectedMonth, selectedYear).map(({ month, year }) =>
          axiosInstant
            .get('/calendar/availability', { params: { userIds: users.map(u => u.value), month, year } })
            .then(res => ({ year, data: res.data as { userId: string; slots: IAvailableSlot[] }[] }))
        )
      )
        .then(parts => {
          const avail: { [userId: string]: IAvailableSlot[] } = {};
          users.forEach(u => {
            avail[u.value] = localizeSlots(
              parts.map(p => ({ items: p.data.find(d => d.userId === u.value)?.slots || [], utcYear: p.year })),
              selectedMonth,
              selectedYear
            );
          });
          setAllAvailabilities(avail);
        })
        .catch((err) => showError(err))
        .finally(() => setLoading(false));
    }
    // Deps are ids, not the objects: `job` and `candidate` are replaced with new
    // object references whenever their fetches resolve, and depending on those
    // identities re-ran this whole effect - and its availability request - over
    // and over. The production log shows the same manager's availability being
    // fetched 15 times in a row because of it.
  }, [isManagerMode, job?._id, candidate?._id, selectedMonth, selectedYear]);

  // RECRUITER MODE: fetch candidate info
  useEffect(() => {
    if (isRecruiterMode && candidateUserId) {
      axiosInstant.get(`/users/${candidateUserId}`)
        .then(res => { if (res.data) setCandidate(res.data); })
        .catch(err => console.log(err.message))
        .finally(() => setLoading(false));
    }
  }, [isRecruiterMode, candidateUserId]);

  // RECRUITER MODE: just fetch candidate's calendar — recruiter picks from candidate's available slots
  useEffect(() => {
    if (isRecruiterMode && candidateUserId) {
      setLoading(true);
      loadLocalMonth(selectedMonth, selectedYear, async (utcMonth, utcYear) =>
        (await axiosInstant.get(endpoints.getCandidateCalendar(candidateUserId, utcMonth - 1, utcYear))).data
      )
        .then(res => {
          const { availableSlots = [], reservedSlots = [] } = res || {};
          setDefaultSlots(availableSlots);
          setReservedSlots(reservedSlots);
          setSelectedHours(getHourBounds(availableSlots));
        })
        .catch(err => showError(err))
        .finally(() => setLoading(false));
    }
  }, [isRecruiterMode, candidateUserId, selectedMonth, selectedYear, refreshFlag]);

  // What each selected participant actually has this month. The grid only ever
  // shows the intersection, so when that is empty the recruiter was shown a bare
  // "No available slots" and had no way to tell a manager with no availability
  // apart from one whose availability simply does not overlap the candidate's.
  const participantAvailability = useMemo(
    () =>
      selectedUserIds.map((id) => {
        const slots = allAvailabilities[id] || [];
        return {
          id,
          name: calendarUsers.find((u) => u.value === id)?.label || "Participant",
          slots,
          hourCount: slots.reduce((n, slot) => n + (slot.time?.length || 0), 0),
        };
      }),
    [selectedUserIds, allAvailabilities, calendarUsers]
  );

  // Someone has availability, but no slot is shared by everyone selected. That is
  // the case worth explaining rather than showing an empty grid.
  const hasUnsharedAvailability =
    isManagerMode &&
    !displaySlots.length &&
    participantAvailability.some((p) => p.hourCount > 0);

  // The table renders one row per hour between these bounds. Manager mode used to
  // leave it at defaultWorkingHours (9-17), so slots outside that window - Aima's
  // are at 0, 1 and 2 - had no row to be drawn in. Derived from whatever is on
  // screen so the two can never disagree; setting it from an effect meant a render
  // where the slots existed but their rows did not.
  const effectiveHours = useMemo(
    () =>
      isManagerMode
        ? displaySlots.length
          ? getHourBounds(displaySlots)
          : defaultWorkingHours
        : selectedHours,
    [isManagerMode, displaySlots, selectedHours]
  );


  useEffect(() => {
    if (isManagerMode && jobId) {
      axiosInstant.get(`/jobs/${jobId}`)
        .then(res => setJob(res.data))
        .catch(() => { setJob(null); setLoading(false); });
    }
  }, [isManagerMode, jobId]);

  const centeredText = (text: string) => (
    <Row>
      <Col span={24}>
        <Typography.Title level={3} style={{ textAlign: "center" }}>
          {text}
        </Typography.Title>
      </Col>
    </Row>
  );

  return (
    <div className="calendar-container">
      <BackBtn />
      <Typography.Title level={2}>
        {candidate?.name ? `Schedule Interview for ${candidate.name}` : "Loading candidate info..."}
      </Typography.Title>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <MonthDropdown {...{ selectedMonth, setSelectedMonth }} />
        <YearDropdown {...{ selectedYear, setSelectedYear }} />
      </div>
      <TimezoneNote />
      {/* Show checkboxes only in manager mode to find common availability across managers + candidate */}
      {isManagerMode && calendarUsers.length > 0 && (
        <div style={{ marginBottom: 16, marginTop: 16 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: '16px' }}>
            Select participants to find common availability:
          </Typography.Text>
          <Checkbox.Group
            options={calendarUsers}
            value={selectedUserIds}
            onChange={(checkedValues) => setSelectedUserIds(checkedValues as string[])}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          />
        </div>
      )}
      {isManagerMode && !loading && hasUnsharedAvailability && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="No slot works for everyone selected"
          description={
            <div>
              <p style={{ marginTop: 0 }}>
                Each person below has availability this month, but no date and hour
                is shared by all of them. Uncheck a participant to widen the search.
              </p>
              {participantAvailability.map((p) => (
                <div key={p.id} style={{ marginBottom: 8 }}>
                  <strong>{p.name}</strong>{" "}
                  {p.hourCount === 0 ? (
                    <span>has not set any availability this month.</span>
                  ) : (
                    <span>
                      — {p.hourCount} slot{p.hourCount === 1 ? "" : "s"}:{" "}
                      {p.slots
                        .slice()
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map(
                          (slot) =>
                            `${slot.date} at ${(slot.time || [])
                              .slice()
                              .sort((x, y) => x - y)
                              .map(getFormattedHours)
                              .join(", ")}`
                        )
                        .join("; ")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          }
        />
      )}
      {/* No slot shared by everyone selected: suggest slots and notify the people who
          would have to open them. */}
      {isManagerMode && !loading && !displaySlots.length && selectedUserIds.length >= 2 && (
        <OverlapSuggestions jobApplicationId={jobApplicationId || ""} userIds={selectedUserIds} />
      )}
      {/* Recruiter -> candidate: offer the recruiter's own free slots. */}
      {isRecruiterMode && candidateUserId && (
        <OfferSlotsPanel jobApplicationId={jobApplicationId || ""} candidateId={candidateUserId} />
      )}
      {!displaySlots.length ? (
        centeredText(
          loading
            ? "Loading..."
            : hasUnsharedAvailability
            ? "No overlapping slots — see the breakdown above"
            : "No available slots"
        )
      ) : (
        <AvailabilityTable
          availabilityView
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          displayMode={DisplayMode.EVERYDAY}
          {...{
            datesArray,
            selectedHours: effectiveHours,
            reservedSlots,
            // Slots shown are driven by the selected participant checkboxes
            defaultSlots: displaySlots,
            onSelect: (date: string, hour: number) =>
              setInterviewDate({ date, hour }),
            onScheduled: refreshCalendar,
          }}
        />
      )}

      {/* Only render modal for the active flow */}
      {isManagerMode && candidate && interviewDate && (
        <ScheduleMeetingModal
          mode="manager"
          candidate={candidate}
          interviewDate={interviewDate}
          setInterviewDate={setInterviewDate}
          jobApplicationId={jobApplicationId!}
          selectedUserIds={selectedUserIds}
          onScheduled={refreshCalendar}
        />
      )}
      {isCandidateMode && candidate && interviewDate && (
        <ScheduleMeetingModal
          mode="candidate"
          candidate={candidate}
          interviewDate={interviewDate}
          setInterviewDate={setInterviewDate}
          jobApplicationId={jobApplicationId!}
          onScheduled={refreshCalendar}
        />
      )}
      {/* Recruiter 1st interview: use candidate mode so scheduleCandidateInterview is called,
          which sets interviewDate + interviewing_recruiter status on the job application */}
      {isRecruiterMode && candidate && interviewDate && (
        <ScheduleMeetingModal
          mode="candidate"
          candidate={candidate}
          interviewDate={interviewDate}
          setInterviewDate={setInterviewDate}
          jobApplicationId={jobApplicationId!}
          onScheduled={refreshCalendar}
        />
      )}
    </div>
  );
};

function intersectSlots(arrays: IAvailableSlot[][]): IAvailableSlot[] {
  if (arrays.length === 0) return [];
  // Build a map: date -> set of hours for each user
  const dateHourMaps = arrays.map(slots => {
    const map = new Map<string, Set<number>>();
    slots.forEach(slot => {
      if (!map.has(slot.date)) map.set(slot.date, new Set());
      slot.time.forEach(hour => map.get(slot.date)!.add(hour));
    });
    return map;
  });

  // Find common dates
  const commonDates = Array.from(dateHourMaps[0].keys()).filter(date =>
    dateHourMaps.every(map => map.has(date))
  );

  // For each common date, find intersection of hours
  const result: IAvailableSlot[] = [];
  for (const date of commonDates) {
    let hours = Array.from(dateHourMaps[0].get(date)!);
    for (let i = 1; i < dateHourMaps.length; i++) {
      hours = hours.filter(h => dateHourMaps[i].get(date)!.has(h));
    }
    if (hours.length) result.push({ date, time: hours });
  }
  return result;
}

export default AvailabilityCalendar;